/**
 * @file BlogParts.tsx
 * @summary Composant blog
 *
 * Brique réutilisable pour les articles de blog (CTA, header, layout…). Style éditorial cohérent avec le design system Chistera.
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, AlertTriangle, ClipboardList, BookOpenCheck, ShieldAlert } from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";
import { getDisplayUpdatedAt, formatFrenchDate } from "@/lib/blog-dates";
import { getBlogImagesForSlug, slugFromUrl, absoluteImageUrl, type BlogImage } from "@/lib/blog-images";


/* ===================== En bref ===================== */
export function EnBref({ children }: { children: ReactNode }) {
  return (
    <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-10 rounded-r-md">
      <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2 flex items-center gap-2">
        <BookOpenCheck className="w-4 h-4" /> En bref
      </p>
      <div className="text-foreground/90 leading-relaxed space-y-2">{children}</div>
    </aside>
  );
}

/* ===================== Quand consulter ? ===================== */
export function QuandConsulter({ title = "Quand consulter ?", children }: { title?: string; children: ReactNode }) {
  return (
    <aside className="my-10 border border-destructive/30 bg-destructive/10 rounded-lg px-6 py-5">
      <h3 className="text-lg font-heading text-destructive mb-3 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> {title}
      </h3>
      <div className="text-foreground/90 leading-relaxed text-[0.95rem] [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_p]:mb-2">
        {children}
      </div>
    </aside>
  );
}

/* ===================== Comment on adapte ça à Chistera ===================== */
export function AdaptationChistera({
  title = "Comment on adapte ça à CrossFit Chistera",
  children,
}: { title?: string; children: ReactNode }) {
  return (
    <aside className="my-10 border border-gold/40 bg-gold/5 rounded-lg px-6 py-5">
      <h3 className="text-lg font-heading text-gold mb-3 flex items-center gap-2">
        <ClipboardList className="w-5 h-5" /> {title}
      </h3>
      <div className="text-foreground/90 leading-relaxed text-[0.95rem] [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_p]:mb-2">
        {children}
      </div>
    </aside>
  );
}

/* ===================== Health disclaimer ===================== */
export function HealthDisclaimer() {
  return (
    <aside className="mt-12 border border-border/60 bg-muted/20 rounded-md px-5 py-4 text-sm text-foreground/70 leading-relaxed">
      <p className="flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          <strong>Information générale.</strong> Cet article ne remplace pas un avis médical, un diagnostic, une prise en charge kinésithérapique ou les consignes de votre chirurgien. En cas de douleur persistante, traumatisme, gonflement, instabilité, perte de force ou doute sur votre situation, consultez un professionnel de santé avant de modifier votre pratique sportive.
        </span>
      </p>
    </aside>
  );
}

/* ===================== Tableau d'adaptations ===================== */
export interface AdaptationRow { standard: string; adapted: string; }
export function AdaptationTable({ rows, headers = ["Mouvement standard", "Adaptation possible"] }: { rows: AdaptationRow[]; headers?: [string, string]; }) {
  return (
    <div className="my-8 overflow-x-auto rounded-md border border-border/50">
      <table className="w-full text-sm">
        <thead className="bg-charcoal/40 text-gold">
          <tr>
            <th className="px-4 py-3 text-left font-heading uppercase tracking-wider text-xs">{headers[0]}</th>
            <th className="px-4 py-3 text-left font-heading uppercase tracking-wider text-xs">{headers[1]}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-muted/10">
              <td className="px-4 py-3 text-foreground/85 align-top">{r.standard}</td>
              <td className="px-4 py-3 text-foreground/85 align-top">{r.adapted}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ===================== Auteur / Reviewer (E-E-A-T) ===================== */
export interface AuthorInfo { name: string; role: string; }
export function AuthorCard({ author, reviewer, publishedAt, updatedAt, readingMinutes }: {
  author: AuthorInfo;
  reviewer?: AuthorInfo;
  publishedAt: string;
  updatedAt?: string;
  readingMinutes?: number;
}) {
  const displayUpdated = getDisplayUpdatedAt(publishedAt, updatedAt);
  const isUpdated = displayUpdated !== publishedAt;
  return (
    <>
      {/* Métadonnées auteur/relecteur masquées visuellement, accessibles aux moteurs de recherche et lecteurs d'écran (E-E-A-T). Les données structurées JSON-LD complètent ce balisage. */}
      <div className="sr-only" aria-hidden="false">
        <p><span>Rédigé par&nbsp;:</span> {author.name} — <span>{author.role}</span></p>
        {reviewer && (
          <p><span>Relu par&nbsp;:</span> {reviewer.name} — <span>{reviewer.role}</span></p>
        )}
        <p>
          Publié le {formatFrenchDate(publishedAt)}
          {isUpdated ? ` · Mis à jour le ${formatFrenchDate(displayUpdated)}` : ""}
        </p>
      </div>
      <div className="my-6 text-xs text-foreground/75">
        Publié le {formatFrenchDate(publishedAt)}
        {isUpdated ? ` · Mis à jour le ${formatFrenchDate(displayUpdated)}` : ""}
        {readingMinutes ? ` · Lecture ~ ${readingMinutes} min` : ""}
      </div>
    </>
  );
}

/* ===================== Sources ===================== */
export interface SourceItem { label: string; href: string; }
export function SourcesSection({ items }: { items: SourceItem[] }) {
  return (
    <section className="my-10">
      <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-4">Sources et références</h2>
      <ul className="space-y-2 text-foreground/80">
        {items.map((s, i) => (
          <li key={i} className="text-sm leading-relaxed">
            <a href={s.href} target="_blank" rel="noopener noreferrer nofollow" className="text-gold hover:underline break-words">
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ===================== Liens internes (pour aller plus loin) ===================== */
export interface InternalLink { label: string; to: string; external?: boolean; }
export function FurtherReading({ items }: { items: InternalLink[] }) {
  return (
    <section className="my-10">
      <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-4">Pour aller plus loin</h2>
      <ul className="space-y-2 text-foreground/85 list-disc pl-5">
        {items.map((l, i) => (
          <li key={i}>
            {l.external ? (
              <a href={l.to} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">{l.label}</a>
            ) : (
              <Link to={l.to} className="text-gold hover:underline">{l.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ===================== FAQ accordéon-light ===================== */
export interface FAQItem { q: string; a: ReactNode; }
export function FAQList({ items }: { items: FAQItem[] }) {
  return (
    <section className="my-10">
      <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-6">Questions fréquentes</h2>
      <div className="space-y-6">
        {items.map((it, i) => (
          <div key={i}>
            <h3 className="text-lg md:text-xl font-heading text-gold mb-2">{it.q}</h3>
            <div className="text-foreground/85 leading-relaxed">{it.a}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ===================== CTA Box (variantes) ===================== */
type CTAVariant = "default" | "douleur" | "reprise" | "competition" | "debutant";

const CTA_TEXT: Record<CTAVariant, { title: string; sub: string; primary: string }> = {
  default: { title: "Envie d'essayer ?", sub: "2 séances d'essai gratuites pour découvrir la box, sans engagement.", primary: "Réserver ma séance d'essai" },
  douleur: { title: "Une douleur, vous voulez bouger sans empirer ?", sub: "Échange gratuit avec un coach pour comprendre votre situation et adapter votre pratique.", primary: "Parler à un coach" },
  reprise: { title: "Vous reprenez après une blessure ou une opération ?", sub: "Discussion gratuite pour évaluer votre niveau actuel et construire un plan de reprise, en lien avec votre kiné.", primary: "Réserver un échange gratuit" },
  competition: { title: "Un objectif compétition en tête ?", sub: "Programmation 12 à 24 semaines, technique, mental. On en parle ?", primary: "Découvrir le coaching individuel" },
  debutant: { title: "Première fois en CrossFit ?", sub: "2 séances d'essai gratuites pour découvrir, sans pression et sans engagement.", primary: "Réserver mon essai gratuit" },
};

export function CTABox({ variant = "default" }: { variant?: CTAVariant }) {
  const t = CTA_TEXT[variant];
  return (
    <section className="my-12 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
      <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">{t.title}</h2>
      <p className="text-foreground/80 mb-6 max-w-xl mx-auto">{t.sub}</p>
      <BlogCTA primaryLabel={t.primary} />
    </section>
  );
}

/* ===================== Layout générique d'article ===================== */
export interface ArticleLayoutProps {
  url: string;
  title: string;
  description: string;
  h1: string;
  category: string;
  jsonLd: object | object[];
  author: AuthorInfo;
  reviewer?: AuthorInfo;
  publishedAt: string;
  updatedAt?: string;
  readingMinutes?: number;
  ctaVariant?: CTAVariant;
  needsHealthDisclaimer?: boolean;
  breadcrumbLabel: string;
  /** Images SEO. Si omis : sélection auto déterministe depuis le pool partagé via le slug. */
  images?: BlogImage[];
  /** Désactive l'image hero affichée en haut (les balises meta restent injectées). */
  hideHeroImage?: boolean;
  children: ReactNode;
}

/**
 * Injecte les images SEO (image[] dans Article JSON-LD) si l'auteur du JSON-LD
 * ne l'a pas fait explicitement. On ne touche pas aux autres types (FAQPage, etc.).
 */
function injectImagesIntoJsonLd(jsonLd: object | object[], imageUrls: string[]): object[] {
  const arr = Array.isArray(jsonLd) ? [...jsonLd] : [jsonLd];
  return arr.map((node: any) => {
    if (!node || typeof node !== "object") return node;
    const t = node["@type"];
    const isArticle = t === "Article" || (Array.isArray(t) && t.includes("Article"));
    if (!isArticle) return node;
    if (node.image) return node; // ne pas écraser
    return { ...node, image: imageUrls };
  });
}

export default function ArticleLayout(props: ArticleLayoutProps) {
  const slug = slugFromUrl(props.url);
  const images = props.images ?? getBlogImagesForSlug(slug, 5);
  const imageUrls = images.map(absoluteImageUrl);
  const jsonLdWithImages = injectImagesIntoJsonLd(props.jsonLd, imageUrls);
  const hero = images[0];
  return (
    <>
      <SEO
        title={props.title}
        description={props.description}
        canonical={props.url}
        jsonLd={jsonLdWithImages}
        ogImages={imageUrls}
        ogType="article"
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-foreground">Blog</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">{props.breadcrumbLabel}</span>
            </nav>

            <header className="mb-8">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">
                {props.category}{props.readingMinutes ? ` · Lecture ~ ${props.readingMinutes} min` : ""}
              </span>
              <h1 className="text-4xl md:text-5xl text-foreground mb-6 font-heading leading-tight">{props.h1}</h1>
            </header>

            {!props.hideHeroImage && hero && (
              <figure className="mb-8 -mx-4 md:mx-0">
                <img
                  src={hero.path}
                  alt={hero.alt}
                  width={hero.width}
                  height={hero.height}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-auto md:rounded-lg border border-border/40 object-cover max-h-[520px]"
                />
                <figcaption className="mt-2 px-4 md:px-0 text-xs text-muted-foreground italic">{hero.caption}</figcaption>
              </figure>
            )}

            <AuthorCard author={props.author} reviewer={props.reviewer} publishedAt={props.publishedAt} updatedAt={props.updatedAt} readingMinutes={props.readingMinutes} />

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85 prose-table:my-0">
              {props.children}
            </div>

            <CTABox variant={props.ctaVariant} />
            {props.needsHealthDisclaimer && <HealthDisclaimer />}
          </article>
        </main>
        <BlogStickyCTA />
        <Footer />
      </div>
    </>
  );
}

/* ===================== Helper JSON-LD ===================== */
export function buildArticleJsonLd({ url, title, description, publishedAt, updatedAt, author, reviewer, faq, images }: {
  url: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt?: string;
  author: AuthorInfo;
  reviewer?: AuthorInfo;
  faq?: { q: string; a: string }[];
  images?: string[];
}) {
  const article: any = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    datePublished: publishedAt,
    dateModified: getDisplayUpdatedAt(publishedAt, updatedAt),
    author: { "@type": "Person", name: author.name, jobTitle: author.role },
    publisher: { "@type": "Organization", name: "CrossFit Chistera", url: "https://crossfitchistera.fr" },
    mainEntityOfPage: url,
    isPartOf: { "@type": "WebSite", name: "CrossFit Chistera", url: "https://crossfitchistera.fr" },
    inLanguage: "fr-FR",
  };
  if (images && images.length > 0) article.image = images;
  if (reviewer) article.reviewedBy = { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role };
  const out: object[] = [article];
  if (faq?.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
  }
  return out;
}

